import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setupAxiosInterceptors } from '../api/axios';
import { isTokenExpired } from '../utils/token';
import authService from '../services/AuthService';

interface AuthContextType {
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setToken: (token: string | null, refreshToken?: string | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  setToken: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setAccessToken] = useState<string | null>(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem('refreshToken'));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const setToken = (access: string | null, refresh: string | null = null) => {
    if (access) localStorage.setItem('token', access);
    else localStorage.removeItem('token');

    if (refresh) localStorage.setItem('refreshToken', refresh);
    else localStorage.removeItem('refreshToken');
    // if (refresh !== null) {
    //   if (refresh) localStorage.setItem('refreshToken', refresh);
    //   else localStorage.removeItem('refreshToken');
    // }

    setAccessToken(access);
    setRefreshToken(refresh);
    setIsAuthenticated(!!access && !isTokenExpired(access));
  };

  const logout = () => {
    setToken(null, null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  // Initial token validation on app load
  useEffect(() => {
    const validateInitialToken = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      
      if (!storedToken) {
        setIsLoading(false);
        setIsAuthenticated(false);
        return;
      }

      // Check if token is expired
      if (isTokenExpired(storedToken)) {
        // Try to refresh the token
        if (storedRefreshToken) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/refresh-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: storedRefreshToken })
            });
            
            if (response.ok) {
              const data = await response.json();
              setToken(data.token, data.refreshToken);
              setIsAuthenticated(true);
            } else {
              // Refresh failed, logout
              logout();
            }
          } catch (error) {
            console.error('Token refresh failed:', error);
            logout();
          }
        } else {
          // No refresh token, logout
          logout();
        }
      } else {
        // Token is valid
        setIsAuthenticated(true);
      }
      
      setIsLoading(false);
    };

    validateInitialToken();
  }, []);

  useEffect(() => {
    setupAxiosInterceptors(setToken, logout);
  }, []);

  return (
    <AuthContext.Provider value={{ token, refreshToken, isAuthenticated, isLoading, setToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
